import React, { FC, useState } from 'react';
import { Input } from 'antd';
import { Button, Notification } from 'Components';
import styles from './TenantLogin.module.scss';
import { ReactComponent as EyeOpen } from '../../../../Icons/new-eye-oppened.svg';
import { ReactComponent as EyeCross } from '../../../../Icons/new-eye-crossed.svg';
import { CognitoUser } from '../pages/Login';
import { CognitoResponseError, SignInRequest } from 'Services/Api/Auth/Types';
import { PhoneNumber } from 'Pages/Auth/Auth.types';
import { useHistory } from 'react-router-dom';
import { useStores } from 'Hooks/useStore';
import {
	COGNITO_ERROR_MSG,
	COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP
} from 'app-config';
import { getAppRoutes } from 'Pages/App/App.constants';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import { firstLoginScreenSchema, loginSchema } from 'Pages/Auth/Login/schema';
import { translateTInfo } from 'i18n/i18n.helpers';

type LoginValues = SignInRequest;

type FirstLoginValues = SignInRequest & PhoneNumber;

type Values = LoginValues | FirstLoginValues;

interface TenantLoginProps {
	setShowResetPassModal: React.Dispatch<React.SetStateAction<boolean>>;
	firstTimeSignIn?: boolean;
	setCognitoUser(user: CognitoUser | null): void;
}

const TenantLogin: FC<TenantLoginProps> = ({
	setShowResetPassModal,
	firstTimeSignIn,
	setCognitoUser
}) => {
	const [showPass, setShowPass] = useState(false);
	const history = useHistory();
	const { authStore } = useStores();
	const { t } = useTranslation();

	const viewPass = () => setShowPass(true);
	const hidePass = () => setShowPass(false);

	const forgotPassword = () => setShowResetPassModal(true);

	const ssoCheck = window.location.search;
	const ssoParams = new URLSearchParams(ssoCheck);
	let deactivated = false;
	let newuser = false;
	if (ssoParams.has('sso')) {
		localStorage.clear();
		(async () => {
			const rawcreds = atob(ssoParams.get('sso')!);
			const creds = rawcreds.split(':');
			const ssologin: SignInRequest = {
				login: creds[0],
				password: creds[1]
			};
			const user = await authStore.signIn(ssologin);
			const url = getAppRoutes().HOME;
			const historyState = {
				userLoggedIn: true
			};
			history.push(url, historyState);
		})();
	}

	if (ssoParams.has('disabled')) {
		if (ssoParams.get('disabled') == 'true') {
			deactivated = true;
		}
	}

	if (ssoParams.has('newuser')) {
		if (ssoParams.get('newuser') == 'true') {
			newuser = true;
		}
	}

	const onSubmit = async (values: Values, helpers: FormikHelpers<Values>) => {
		helpers.setStatus('');
		try {
			localStorage.clear();
			const user = await authStore.signIn(values);

			if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
				setCognitoUser(user);
			} else {
				const state = history.location.state as {
					returnUrl?: string;
				} | null;

				const url = state?.returnUrl ?? getAppRoutes().HOME;

				const historyState = {
					userLoggedIn: true
				};
				history.push(url, historyState);
			}
		} catch (e) {
			const error = e as CognitoResponseError;
			helpers.setSubmitting(false);

			if (
				error.message === COGNITO_ERROR_MSG.TEMPORARY_PASSWORD_EXPIRED
			) {
				history.push(getAppRoutes().RESET_TEMPORARY_PASSWORD, {
					cognitoLoginTemporaryPasswordExpired: values.login
				});
				return;
			}

			const cognitoError =
				COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP[error.message];

			if (cognitoError) {
				helpers.setStatus(t(cognitoError));
				return;
			}

			switch (error.code) {
				case 'NotAuthorizedException':
				case 'UserNotFoundException':
					helpers.setStatus(t('auth.login.errors.notAuthorized'));
					break;
				default:
					Notification.error({
						description: t('errorCodes.genericErrorMessage')
					});
					break;
			}
		}
	};

	const initialValues: Values = {
		login: '',
		password: ''
	};

	const schema = firstTimeSignIn ? firstLoginScreenSchema : loginSchema;

	const ErrorComponent = (
		<div className={styles.errorMsg}>
			<strong>Invalid username or password.</strong> Try again, reset your
			password, or contact your manager.
		</div>
	);

	return (
		<div className={styles.container}>
			<div className={styles.title}>Sign in</div>
			<Formik
				initialValues={initialValues}
				onSubmit={onSubmit}
				validationSchema={schema}
			>
				{(formik) => (
					<Form>
						<div className={styles.field}>
							<label>Username</label>
							<Input
								name="login"
								size="large"
								placeholder="Enter your username"
								className={styles.inputField}
								value={formik.values.login}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							/>
							{formik.touched.login && formik.errors.login && (
								<div className={styles.errorMsg}>
									{translateTInfo(
										formik.errors.login as any,
										t
									)}
								</div>
							)}
						</div>

						<div className={styles.field}>
							<label>Password</label>
							<Input
								name="password"
								type={showPass ? 'text' : 'password'}
								placeholder="Enter your password"
								className={styles.innerInput}
								suffix={
									showPass ? (
										<EyeCross onClick={hidePass} />
									) : (
										<EyeOpen onClick={viewPass} />
									)
								}
								value={formik.values.password}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
							/>
							{formik.touched.password &&
								formik.errors.password && (
									<div className={styles.errorMsg}>
										{translateTInfo(
											formik.errors.password as any,
											t
										)}
									</div>
								)}
						</div>

						<Button
							type="primary"
							shape="round"
							htmlType="submit"
							loading={formik.isSubmitting}
						>
							Log in
						</Button>
						{formik.status && ErrorComponent}
					</Form>
				)}
			</Formik>

			{!firstTimeSignIn && (
				<div className={styles.forgotPassword} onClick={forgotPassword}>
					Forgot your password?
				</div>
			)}
		</div>
	);
};

export default TenantLogin;
